import { useQuery } from "@tanstack/react-query";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";

export interface EpisodeSummaryResult {
  readonly summary: string | null;
  readonly isLoading: boolean;
}

interface EpisodeSummaryResponse {
  readonly summary?: string | null;
  readonly text?: string | null;
}

/**
 * Fetch an episode summary via `episodes.summary.get`. Treats 404 / empty
 * response as "no summary" — the caller decides whether to render a
 * placeholder or hide the section entirely.
 */
export function useEpisodeSummary(entityId: string | undefined): EpisodeSummaryResult {
  const runtime = useAppRuntime();
  const { data, isLoading } = useQuery({
    queryKey: ["episode-summary", entityId],
    queryFn: async () => {
      try {
        return await runtime.transport.rpc<EpisodeSummaryResponse>(
          "episodes.summary.get",
          { episode_id: entityId },
        );
      } catch {
        return { summary: null };
      }
    },
    enabled: !!entityId,
  });

  const summary = data?.summary ?? data?.text ?? null;
  return {
    summary: typeof summary === "string" && summary.length > 0 ? summary : null,
    isLoading,
  };
}
